import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch, TextField, Typography,
} from '@mui/material';
import { api } from '../../services/api';
import {
  SCHEDULE_REPEAT_OPTIONS,
  describeRepeat,
  isValidTimeZone,
  siteTimezonesFromSites,
} from '../../utils/optimizeScheduleUtils';

/**
 * The site's timezone(s) from the site record (GraphQL), never the
 * browser's, looked up each time a schedule dialog opens.
 * `loaded` turns true once the lookup finished (ok or not).
 */
export function useSiteTimezones(open) {
  const [timezones, setTimezones] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoaded(false);
    setError('');
    api.getProjectSites()
      .then((sites) => {
        if (cancelled) return;
        const zones = siteTimezonesFromSites(sites);
        setTimezones(zones);
        if (zones.length === 0) setError('The site record has no valid timezone, so the schedule time cannot be placed. Fix the site timezone first.');
      })
      .catch((err) => {
        if (!cancelled) setError(`Could not read the site timezone: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [open]);
  return { timezones, loaded, error };
}

/** Timezone choices: the site's, plus the stored one if the site changed since. */
export function useTimezoneOptions(timezones, current) {
  return useMemo(() => {
    const out = [...timezones];
    if (current && isValidTimeZone(current) && !out.includes(current)) out.push(current);
    return out;
  }, [timezones, current]);
}

/** Problems with the "When" part of a schedule form, first one shown. */
export function scheduleWhenProblems(form) {
  const problems = [];
  if (!form.name.trim()) problems.push('Give the schedule a name.');
  if (!isValidTimeZone(form.timezone)) problems.push('A valid site timezone is required.');
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(form.startLocal)) problems.push('Pick a start date and time.');
  return problems;
}

/**
 * Name, site timezone, start (site time), repeat and — when editing —
 * enabled/paused. Shared by the optimize and iperf schedule dialogs.
 *
 * @param {object} props
 * @param {object} props.form { name, timezone, startLocal, repeat, enabled }
 * @param {(patch:object) => void} props.onChange
 * @param {string[]} props.tzOptions
 * @param {boolean} props.editing
 */
export default function ScheduleWhenFields({ form, onChange, tzOptions, editing }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>When</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField size="small" label="Name" value={form.name} onChange={(e) => onChange({ name: e.target.value })} inputProps={{ maxLength: 120 }} fullWidth />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl size="small" fullWidth disabled={tzOptions.length <= 1}>
            <InputLabel>Site timezone</InputLabel>
            <Select label="Site timezone" value={tzOptions.includes(form.timezone) ? form.timezone : ''} onChange={(e) => onChange({ timezone: e.target.value })}>
              {tzOptions.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            size="small"
            label="Starts (site time)"
            type="datetime-local"
            value={form.startLocal}
            onChange={(e) => onChange({ startLocal: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl size="small" fullWidth>
            <InputLabel>Repeat</InputLabel>
            <Select label="Repeat" value={form.repeat} onChange={(e) => onChange({ repeat: e.target.value })}>
              {SCHEDULE_REPEAT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Alert severity="info" icon={false} sx={{ mt: 1.5 }}>
        {describeRepeat(form.repeat, form.startLocal)} ({form.timezone || 'site time'})
      </Alert>
      {editing && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Switch checked={form.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} />}
          label={form.enabled ? 'Enabled' : 'Paused'}
        />
      )}
    </Box>
  );
}
